// frontend/src/pages/ProcessMapBuilder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    PlusIcon, 
    TrashIcon, 
    ArrowUpIcon, 
    ArrowDownIcon,
    LightBulbIcon,
    ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';

const ProcessMapBuilder = () => {
    const { mapId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [processMap, setProcessMap] = useState(null);
    const [steps, setSteps] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [newStep, setNewStep] = useState({
        name: '',
        description: '',
        owner: '',
        role: '',
        time_estimate_minutes: '',
        is_manual: false,
        is_ai_opportunity: false,
        systems_used: [],
        documents_used: [],
        risks: ''
    });
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');

    useEffect(() => {
        if (mapId) {
            loadProcessMap();
        } else {
            setLoading(false);
        }
        loadTemplates();
    }, [mapId]);

    const loadProcessMap = async () => {
        try {
            const response = await axios.get(`/process-mapping/${mapId}`);
            const data = response.data.data;
            setProcessMap(data);
            setSteps(data.steps || []);
            setComments(data.comments || []);
        } catch (error) {
            toast.error('Failed to load process map');
            navigate('/business');
        } finally {
            setLoading(false);
        }
    };

    const loadTemplates = async () => {
        try {
            const response = await axios.get('/process-mapping/templates');
            setTemplates(response.data.data || []);
        } catch (error) {
            console.error('Failed to load templates:', error);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = {
                name: processMap?.name || 'Untitled Process Map',
                description: processMap?.description || '',
                steps: steps.map((step, index) => ({
                    step_number: index + 1,
                    ...step
                }))
            };

            let response;
            if (mapId) {
                response = await axios.put(`/process-mapping/${mapId}`, data);
            } else {
                response = await axios.post('/process-mapping', {
                    ...data,
                    business_assessment_id: processMap?.business_assessment_id || 1
                });
            }

            toast.success('Process map saved successfully');
            if (!mapId && response.data?.data?.id) {
                navigate(`/process-mapping/${response.data.data.id}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async () => {
        if (!window.confirm('Submit this process map for review?')) return;
        
        try {
            await axios.post(`/process-mapping/${mapId}/submit`);
            toast.success('Process map submitted for review');
            loadProcessMap();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit');
        }
    };

    const handleAddStep = () => {
        setSteps([...steps, {
            ...newStep,
            step_number: steps.length + 1
        }]);
        setNewStep({
            name: '',
            description: '',
            owner: '',
            role: '',
            time_estimate_minutes: '',
            is_manual: false,
            is_ai_opportunity: false,
            systems_used: [],
            documents_used: [],
            risks: ''
        });
    };

    const handleRemoveStep = (index) => {
        const newSteps = steps.filter((_, i) => i !== index);
        newSteps.forEach((step, i) => step.step_number = i + 1);
        setSteps(newSteps);
    };

    const handleMoveStep = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= steps.length) return;
        
        const newSteps = [...steps];
        [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
        newSteps.forEach((step, i) => step.step_number = i + 1);
        setSteps(newSteps);
    };

    const handleStepChange = (index, field, value) => {
        const newSteps = [...steps];
        newSteps[index] = { ...newSteps[index], [field]: value };
        setSteps(newSteps);
    };

    const handleApplyTemplate = async (templateId) => {
        if (!mapId) {
            toast.error('Please save the process map first before applying a template');
            return;
        }
        try {
            await axios.post('/process-mapping/apply-template', {
                mapId: mapId,
                templateId: templateId
            });
            toast.success('Template applied successfully');
            setShowTemplateModal(false);
            loadProcessMap();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to apply template');
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        
        try {
            await axios.post(`/process-mapping/${mapId}/comment`, {
                comment: newComment
            });
            setNewComment('');
            loadProcessMap();
        } catch (error) {
            toast.error('Failed to add comment');
        }
    };

    const canEdit = processMap?.canEdit !== false;
    const canSubmit = processMap?.canSubmit;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading process map...</div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {mapId ? 'Edit Process Map' : 'New Process Map'}
                        </h1>
                        {processMap && (
                            <div className="flex items-center space-x-3 mt-1">
                                <span className="text-sm text-gray-600">
                                    Status: 
                                    <span className="font-medium ml-1 capitalize">
                                        {processMap.status}
                                    </span>
                                </span>
                                {processMap.assessment?.business && (
                                    <span className="text-sm text-gray-500">
                                        • {processMap.assessment.business.name}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        {canSubmit && (
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                            >
                                Submit for Review
                            </button>
                        )}
                        {mapId && (
                            <button
                                onClick={() => setShowTemplateModal(true)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors"
                            >
                                Apply Template
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Map'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Steps Area */}
                <div className="lg:col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">Process Steps</h2>
                            {canEdit && (
                                <button
                                    onClick={handleAddStep}
                                    className="px-3 py-1 text-sm bg-black text-white rounded-md hover:bg-gray-800 transition-colors flex items-center space-x-1"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    <span>Add Step</span>
                                </button>
                            )}
                        </div>

                        {steps.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <p>No steps added yet.</p>
                                {canEdit && (
                                    <button
                                        onClick={handleAddStep}
                                        className="mt-2 text-black underline"
                                    >
                                        Add your first step
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {steps.map((step, index) => (
                                    <div 
                                        key={index} 
                                        className={`border rounded-lg p-4 ${
                                            step.is_ai_opportunity ? 'border-green-300 bg-green-50' : 'border-gray-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3">
                                                    <span className="text-sm font-medium text-gray-500">
                                                        Step {step.step_number || index + 1}
                                                    </span>
                                                    {canEdit ? (
                                                        <input
                                                            type="text"
                                                            value={step.name}
                                                            onChange={(e) => handleStepChange(index, 'name', e.target.value)}
                                                            className="text-sm font-medium text-gray-900 bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-black"
                                                            placeholder="Step name"
                                                        />
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {step.name || 'Untitled Step'}
                                                        </span>
                                                    )}
                                                    {step.is_ai_opportunity && (
                                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                                                            🤖 AI Opportunity
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-2 space-y-2">
                                                    {canEdit ? (
                                                        <textarea
                                                            value={step.description || ''}
                                                            onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                                            className="w-full text-sm text-gray-600 bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-black"
                                                            placeholder="Step description"
                                                            rows={1}
                                                        />
                                                    ) : (
                                                        <p className="text-sm text-gray-600">{step.description}</p>
                                                    )}
                                                    
                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                        <input
                                                            type="text"
                                                            value={step.owner || ''}
                                                            onChange={(e) => handleStepChange(index, 'owner', e.target.value)}
                                                            placeholder="Owner (e.g. Sales Rep)"
                                                            className="p-1 border border-gray-200 rounded"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={step.role || ''}
                                                            onChange={(e) => handleStepChange(index, 'role', e.target.value)}
                                                            placeholder="Role"
                                                            className="p-1 border border-gray-200 rounded"
                                                        />
                                                    </div>

                                                    <div className="flex items-center space-x-4 text-xs pt-1">
                                                        <label className="flex items-center space-x-1 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={step.is_manual || false}
                                                                onChange={(e) => handleStepChange(index, 'is_manual', e.target.checked)}
                                                                className="rounded text-black"
                                                            />
                                                            <span>Manual Step</span>
                                                        </label>

                                                        <label className="flex items-center space-x-1 cursor-pointer text-green-700 font-medium">
                                                            <input
                                                                type="checkbox"
                                                                checked={step.is_ai_opportunity || false}
                                                                onChange={(e) => handleStepChange(index, 'is_ai_opportunity', e.target.checked)}
                                                                className="rounded text-green-600"
                                                            />
                                                            <span>AI Opportunity</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {canEdit && (
                                                <div className="flex items-center space-x-1 ml-4">
                                                    <button
                                                        onClick={() => handleMoveStep(index, -1)}
                                                        disabled={index === 0}
                                                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                                    >
                                                        <ArrowUpIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleMoveStep(index, 1)}
                                                        disabled={index === steps.length - 1}
                                                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                                    >
                                                        <ArrowDownIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveStep(index)}
                                                        className="p-1 text-red-400 hover:text-red-600"
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* AI Suggestions */}
                    {processMap?.aiSuggestions?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                                <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                                <span>AI Suggestions</span>
                            </h3>
                            <div className="mt-2 space-y-2">
                                {processMap.aiSuggestions.map((suggestion) => (
                                    <div key={suggestion.id} className="text-sm p-3 bg-yellow-50 rounded-lg">
                                        <p className="font-medium text-gray-900">{suggestion.title}</p>
                                        <p className="text-gray-600 text-xs mt-1">{suggestion.description}</p>
                                        <button
                                            className="mt-2 text-xs text-green-600 font-medium hover:underline"
                                            onClick={() => {
                                                axios.put(`/process-mapping/ai-suggestion/${suggestion.id}/accept`)
                                                    .then(() => {
                                                        toast.success('Suggestion accepted!');
                                                        loadProcessMap();
                                                    })
                                                    .catch(() => toast.error('Failed to accept suggestion'));
                                            }}
                                        >
                                            Accept Suggestion →
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Comments */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                            <ChatBubbleLeftIcon className="w-5 h-5" />
                            <span>Comments</span>
                        </h3>
                        <div className="mt-2 max-h-48 overflow-y-auto space-y-2">
                            {comments.length === 0 ? (
                                <p className="text-sm text-gray-500">No comments yet</p>
                            ) : (
                                comments.map((comment) => (
                                    <div key={comment.id} className="text-sm border-b border-gray-100 pb-2">
                                        <p className="text-gray-800">{comment.comment}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {comment.User?.first_name} {comment.User?.last_name} • 
                                            {new Date(comment.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                        {mapId && (
                            <div className="mt-3 flex space-x-2">
                                <input
                                    type="text"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add comment..."
                                    className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                                />
                                <button
                                    onClick={handleAddComment}
                                    className="px-3 py-1 text-sm bg-black text-white rounded-md hover:bg-gray-800"
                                >
                                    Send
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Map Info */}
                    {processMap && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h3 className="text-sm font-medium text-gray-900">Map Details</h3>
                            <div className="mt-2 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Status</span>
                                    <span className="font-medium capitalize">{processMap.status}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Steps</span>
                                    <span className="font-medium">{steps.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">AI Opportunities</span>
                                    <span className="font-medium">
                                        {steps.filter(s => s.is_ai_opportunity).length}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Version</span>
                                    <span className="font-medium">
                                        {processMap.versions?.[0]?.version_number || 1}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Template Selection Modal */}
            {showTemplateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg p-6 max-w-xl w-full space-y-4">
                        <div className="flex justify-between items-center border-b border-gray-100 pb-3">
                            <h3 className="text-lg font-bold text-gray-900">Apply Template</h3>
                            <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-black text-xl">&times;</button>
                        </div>

                        <div className="space-y-3 max-h-60 overflow-y-auto">
                            {templates.map((tpl) => (
                                <div key={tpl.id} className="p-3 border border-gray-200 rounded-lg flex justify-between items-center hover:border-black">
                                    <div>
                                        <h4 className="font-semibold text-sm text-gray-900">{tpl.name}</h4>
                                        <p className="text-xs text-gray-500">{tpl.description}</p>
                                    </div>
                                    <button
                                        onClick={() => handleApplyTemplate(tpl.id)}
                                        className="px-3 py-1 bg-black text-white text-xs rounded hover:bg-gray-800"
                                    >
                                        Apply
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessMapBuilder;
